import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  fetchAdminDashboardStats,
  getReportedUsersAPI,
  unlistItemAPI,
  updateAdminItemListingStatusAPI,
  updateUserAdminStatus,
  updateReviewStatus,
  updateReportStatus,
} from "../../services/adminService";
import { SuccessToaster, ErrorToaster } from "../../helpers/toasterHelper";
import { RootState } from "../store";
import { broadcastAlert, sendUserAlert } from "../../services/adminService";
import { SUCCESS_MESSAGES, ERROR_MESSAGES, USER_STATUS, ITEM_STATUS } from "../../utils/constants";
import { ReviewType } from "../../types/admin";

interface AdminState {
  totalUsers: number;
  totalActiveUsers: number;
  totalItems: number;
  totalActiveItems: number;
  loading: boolean;
  error: string | null;
  reportedUsers: any[];
  reportedUsersNextPageKey: string | null;
  reportedUsersHasMore: boolean;
  reportedUsersLoading: boolean;
}

const initialState: AdminState = {
  totalUsers: 0,
  totalActiveUsers: 0,
  totalItems: 0,
  totalActiveItems: 0,
  loading: false,
  error: null,
  reportedUsers: [],
  reportedUsersNextPageKey: null,
  reportedUsersHasMore: true,
  reportedUsersLoading: false,
};

export const getAdminDashboardStats = createAsyncThunk(
  "admin/getAdminDashboardStats",
  async (
    _, thunkAPI
  ) => {
    try {
      const response = await fetchAdminDashboardStats();
      SuccessToaster(SUCCESS_MESSAGES.STATS_LOADED_SUCCESSFULLY);
      return response;
    } catch (error: any) {
      return thunkAPI.rejectWithValue(error.message || ERROR_MESSAGES.FAILED_TO_FETCH_STATS);
    }
  }
);

export const sendBroadcastAlert = createAsyncThunk(
  "admin/broadcastAlert",
  async (
    { title, message, severity }: { title: string; message: string; severity: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await broadcastAlert(title, message, severity);
      SuccessToaster(SUCCESS_MESSAGES.BROADCAST_MESSAGE_SENT_SUCCESSFULLY);
      return response;
    } catch (error: any) {
      ErrorToaster(error.message || ERROR_MESSAGES.FAILED_TO_SEND_BROADCAST_MESSAGE);
      return rejectWithValue(error.message);
    }
  }
);

export const sendUserAlertAction = createAsyncThunk(
  "admin/sendUserAlert",
  async (
    { userId, title, message, severity }: { userId: string; title: string; message: string; severity: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await sendUserAlert(userId, title, message, severity);
      SuccessToaster(SUCCESS_MESSAGES.ALERT_SENT_TO_USER_SUCCESSFULLY);
      return response;
    } catch (error: any) {
      ErrorToaster(error.message || ERROR_MESSAGES.FAILED_TO_SEND_ALERT_TO_USER);
      return rejectWithValue(error.message);
    }
  }
);

export const unlistItem = createAsyncThunk(
  "admin/unlistItem",
  async (itemId: string, { rejectWithValue }) => {
    try {
      const response = await unlistItemAPI(itemId);
      SuccessToaster(SUCCESS_MESSAGES.ITEM_UNLISTED_SUCCESSFULLY);
      return response;
    } catch (error: any) {
      ErrorToaster(error.message || ERROR_MESSAGES.FAILED_TO_UNLIST_ITEM);
      return rejectWithValue(error.message);
    }
  }
);

export const updateAdminItemListingStatus = createAsyncThunk(
  "admin/updateAdminItemListingStatus",
  async (
    { itemId, status }: { itemId: string; status: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await updateAdminItemListingStatusAPI(itemId, status);
      const successMsg =
        status === ITEM_STATUS.LISTED
          ? SUCCESS_MESSAGES.ITEM_STATUS_UPDATED_SUCCESSFULLY
          : SUCCESS_MESSAGES.ITEM_UNLISTED_SUCCESSFULLY;
      SuccessToaster(successMsg);
      return response;
    } catch (error: any) {
      ErrorToaster(error.message || ERROR_MESSAGES.FAILED_TO_UPDATE_ITEM_STATUS);
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserAdminStatusAction = createAsyncThunk(
  "admin/updateUserAdminStatus",
  async (
    { userId, status, blockedByAdmin }: { userId: string; status: string; blockedByAdmin: boolean },
    { rejectWithValue }
  ) => {
    try {
      const response = await updateUserAdminStatus(userId, status, blockedByAdmin);
      fetchReportedUsersAction({})
      const successMsg = status === USER_STATUS.ACTIVE ? SUCCESS_MESSAGES.USER_ACTIVATED_SUCCESSFULLY : SUCCESS_MESSAGES.USER_DEACTIVATED_SUCCESSFULLY;
      SuccessToaster(successMsg);
      return { userId, ...response };
    } catch (error: any) {
      const errorMsg = status === USER_STATUS.ACTIVE ? ERROR_MESSAGES.FAILED_TO_ACTIVATE_USER : ERROR_MESSAGES.FAILED_TO_DEACTIVATE_USER;
      ErrorToaster(error.message || errorMsg);
      return rejectWithValue(error.message);
    }
  }
);

export const updateUserReportStatus = createAsyncThunk(
  "admin/updateUserReportStatus",
  async ({ reviewId }: { reviewId: string }, { rejectWithValue }) => {
    try {
      const response = await updateReportStatus(reviewId);
      SuccessToaster(SUCCESS_MESSAGES.REPORT_STATUS_UPDATED_SUCCESSFULLY);
      return response ;
    } catch(error: any){
      ErrorToaster(error.message || ERROR_MESSAGES.FAILED_TO_UPDATE_REPORT_STATUS);
      return rejectWithValue(error.message);
    }
  }
);

export const updateReviewStatusAction = createAsyncThunk(
  "admin/updateReviewStatus",
  async (
    { reviewId, status, reviewType }: { reviewId: string; status: string; reviewType: ReviewType },
    { rejectWithValue }
  ) => {
    try {
      const response = await updateReviewStatus(reviewId, status, reviewType);
      SuccessToaster(SUCCESS_MESSAGES.REVIEW_STATUS_UPDATED);
      return response;
    } catch (error: any) {
      ErrorToaster(error.message || ERROR_MESSAGES.REVIEW_STATUS_FAILED);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchReportedUsersAction = createAsyncThunk(
  "admin/fetchReportedUsers",
  async ({ nextPageKey, limit = 10, isLoadMore = false }: { nextPageKey?: string | null; limit?: number; isLoadMore?: boolean }, { rejectWithValue }) => {
    try {
      const response: any = await getReportedUsersAPI("USER", nextPageKey, limit);
      return { data: response, isLoadMore };
    } catch (error: any) {
      ErrorToaster(error.message || ERROR_MESSAGES.REPORTED_USER_FETCH_FAILED);
      return rejectWithValue(error.message);
    }
  }
);

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getAdminDashboardStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAdminDashboardStats.fulfilled, (state, action) => {
        state.loading = false;
        state.totalUsers = action.payload.totalUsers;
        state.totalActiveUsers = action.payload.totalActiveUsers;
        state.totalItems = action.payload.totalItems;
        state.totalActiveItems = action.payload.totalActiveItems;
      })
      .addCase(getAdminDashboardStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(sendBroadcastAlert.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendBroadcastAlert.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sendBroadcastAlert.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(sendUserAlertAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(sendUserAlertAction.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(sendUserAlertAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(unlistItem.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(unlistItem.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(unlistItem.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateAdminItemListingStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateAdminItemListingStatus.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateAdminItemListingStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(updateUserAdminStatusAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateUserAdminStatusAction.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateUserAdminStatusAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(fetchReportedUsersAction.pending, (state) => {
        state.reportedUsersLoading = true;
        state.error = null;
      })
      .addCase(fetchReportedUsersAction.fulfilled, (state, action) => {
        state.reportedUsersLoading = false;
        if (action.payload.isLoadMore) {
          state.reportedUsers = [...state.reportedUsers, ...(action.payload?.data?.reviews || [])];
        } else {
          state.reportedUsers = action.payload?.data?.reviews || [];
        }
        state.reportedUsersNextPageKey = action.payload?.data?.nextPageKey || null;
        state.reportedUsersHasMore = !!action.payload?.data?.nextPageKey;
      })
      .addCase(fetchReportedUsersAction.rejected, (state, action) => {
        state.reportedUsersLoading = false;
        state.error = action.payload as string;
      })
      .addCase(updateReviewStatusAction.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateReviewStatusAction.fulfilled, (state) => {
        state.loading = false;
      })
      .addCase(updateReviewStatusAction.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export default adminSlice.reducer;
export const adminSelector = (state: RootState) => state.admin;
